import { useEffect, useState } from "react";
import { Check, X, RotateCcw, Trophy, ArrowRight, Users } from "lucide-react";
import { AdvancedQuestion, answerText } from "@/components/player/quizTypes";

const TYPE_LABEL = { mcq: "Choose one", tf: "True or false", matching: "Match the pairs", fill: "Fill in the blanks", dnd: "Drag the words into place" };

const Result = ({ layer, answers, onRetry, stats }) => {
  const qs = layer.questions;
  const pts = layer.points || 1;
  const correct = answers.filter(Boolean).length;
  const pct = Math.round((correct / qs.length) * 100);
  const passed = pct >= (layer.pass ?? 70);
  return (
    <div className="text-center" data-testid="quiz-result">
      <span className={`mx-auto grid h-16 w-16 place-items-center rounded-full text-white ${passed ? "bg-[#5FA82E]" : "bg-[#F7933E]"}`}><Trophy className="h-7 w-7" /></span>
      <p className="mt-4 font-display text-4xl font-extrabold" data-testid="quiz-score">{pct}%</p>
      <p className="mt-1 text-sm text-[#64748B]">{correct} of {qs.length} correct · {correct * pts}/{qs.length * pts} points</p>
      <p className={`mt-3 font-display text-xl font-bold ${passed ? "text-[#5FA82E]" : "text-[#A62B8A]"}`} data-testid="quiz-verdict">{passed ? "Passed — great work!" : `Not quite — you need ${layer.pass ?? 70}% to pass`}</p>
      <ul className="mx-auto mt-5 max-w-md space-y-1.5 text-left">
        {qs.map((q, i) => (
          <li key={i} className="flex items-start gap-2 text-xs">
            {answers[i] ? <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[#5FA82E]" /> : <X className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[#A62B8A]" />}
            <span>{q.prompt.replace(/___/g, "…")} <span className="text-[#64748B]">— {answerText(q)}</span></span>
          </li>
        ))}
      </ul>
      {stats && (
        <div className="neu-inset mx-auto mt-5 flex max-w-md flex-wrap justify-center gap-x-5 gap-y-1 rounded-2xl px-4 py-3 text-xs" data-testid="quiz-community-stats">
          <span className="flex items-center gap-1 font-semibold text-[#3D3DF0]"><Users className="h-3.5 w-3.5" />All readers</span>
          <span><b>{stats.attempts}</b> attempts</span><span>avg <b>{stats.avg_score}%</b></span><span>pass rate <b>{stats.pass_rate}%</b></span>
        </div>
      )}
      {layer.retry !== false && <button onClick={onRetry} data-testid="quiz-retry-btn" className="neu-btn-primary mt-6 inline-flex items-center gap-2 rounded-full px-6 py-2.5 text-sm font-bold"><RotateCcw className="h-4 w-4" />Retry quiz</button>}
    </div>
  );
};

export const QuizRunner = ({ layer, onComplete }) => {
  const qs = layer.questions || [];
  const [i, setI] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [picked, setPicked] = useState(null);
  const [stats, setStats] = useState(null);
  const [checked, setChecked] = useState(null);
  const finished = qs.length > 0 && answers.length === qs.length;
  useEffect(() => {
    if (!finished || !onComplete) return;
    const results = answers.map(Boolean);
    const correct = results.filter(Boolean).length;
    const score = Math.round((correct / qs.length) * 100);
    Promise.resolve(onComplete({ correct, total: qs.length, score, passed: score >= (layer.pass ?? 70), results })).then((s) => s && setStats(s)).catch(() => {});
  }, [finished]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!qs.length) return <p className="p-8 text-sm text-[#64748B]">This quiz has no questions yet.</p>;
  const done = answers.length === qs.length;
  const q = qs[i];
  const basic = q && (q.type === "mcq" || q.type === "tf" || !q.type);
  const choose = (j) => { if (picked === null) { setPicked(j); setChecked({ correct: j === q.answer }); } };
  const next = () => { setAnswers([...answers, checked.correct]); setPicked(null); setChecked(null); if (i < qs.length - 1) setI(i + 1); };
  const retry = () => { setI(0); setAnswers([]); setPicked(null); setChecked(null); setStats(null); };

  return (
    <div className="p-8" data-testid="quiz-runner">
      <p className="font-mono text-[10px] uppercase tracking-widest text-[#A62B8A]">{layer.title || "Quiz"}</p>
      {done ? <div className="mt-4"><Result layer={layer} answers={answers} onRetry={retry} stats={stats} /></div> : (
        <>
          <div className="mt-3 flex gap-1.5">{qs.map((_, k) => <span key={k} className={`h-1.5 flex-1 rounded-full ${k < answers.length ? "bg-[#3D3DF0]" : k === i ? "bg-[#A62B8A]/50" : "bg-slate-200"}`} />)}</div>
          <p className="mt-2 text-xs text-[#64748B]" data-testid="quiz-progress">Question {i + 1} of {qs.length} · {TYPE_LABEL[q.type] || "Choose one"}</p>
          <h3 className="mt-3 font-display text-2xl font-bold" data-testid="quiz-question">{basic || q.type === "matching" ? q.prompt : TYPE_LABEL[q.type]}</h3>
          {!basic && <AdvancedQuestion key={`${i}-${answers.length}`} q={q} locked={!!checked} onCheck={(c) => setChecked({ correct: c })} />}
          {basic && <div className={`mt-5 grid gap-3 ${q.type === "tf" ? "grid-cols-2" : ""}`}>
            {q.options.map((o, j) => {
              const state = picked === null ? "" : j === q.answer ? "correct" : j === picked ? "wrong" : "dim";
              return (
                <button key={j} onClick={() => choose(j)} disabled={picked !== null} data-testid={`quiz-option-${j}`}
                  className={`flex items-center justify-between rounded-2xl px-4 py-3 text-left text-sm font-semibold transition-all ${state === "correct" ? "bg-[#5FA82E] text-white" : state === "wrong" ? "bg-[#A62B8A] text-white" : state === "dim" ? "neu-flat opacity-50" : "neu-btn"}`}>
                  {o}{state === "correct" ? <Check className="h-4 w-4" /> : state === "wrong" ? <X className="h-4 w-4" /> : null}
                </button>
              );
            })}
          </div>}
          {checked && (
            <div className="mt-5 flex flex-wrap items-center justify-between gap-3" data-testid="quiz-feedback">
              <p className={`text-sm font-semibold ${checked.correct ? "text-[#5FA82E]" : "text-[#A62B8A]"}`}>{checked.correct ? "Correct!" : `Incorrect — the answer is “${answerText(q)}”.`} <span className="font-normal text-[#334155]">{q.feedback}</span></p>
              <button onClick={next} data-testid="quiz-next-btn" className="neu-btn-primary inline-flex items-center gap-2 rounded-full px-5 py-2 text-sm font-bold">{i < qs.length - 1 ? "Next" : "See score"}<ArrowRight className="h-4 w-4" /></button>
            </div>
          )}
        </>
      )}
    </div>
  );
};
